import { useState, useEffect, useCallback } from "react";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import AsyncSelect from "react-select/async";
import { Button, OutlineButton } from "components/button";
import type {
  CampaignFilters,
  CampaignType,
  FilterOptions,
  LocationCombo,
  Recipient,
  SelectedCustomer,
} from "../types/ICampaign";
import { getFilterOptions, lookupRecipientContact } from "../api/campaigns";
import { useFilterModalReducer } from "../hooks/useFilterModalReducer";
import { useRecipientPreview } from "../hooks/useRecipientPreview";
import { useCustomerSearch, CustomerOption } from "../hooks/useCustomerSearch";
import CollapsibleSection from "./CollapsibleSection";
import TypeaheadFilter from "./TypeaheadFilter";
import RecipientsTable from "./RecipientsTable";

interface FilterRecipientsModalProps {
  isOpen: boolean;
  filters: CampaignFilters;
  selectedCustomers: SelectedCustomer[];
  onApply: (filters: CampaignFilters, selectedCustomers: SelectedCustomer[]) => void;
  onCancel: () => void;
  campaignType?: CampaignType;
}

const ASYNC_SELECT_STYLES = {
  control: (base: Record<string, unknown>) => ({
    ...base,
    borderColor: "#d1d5db",
    minHeight: 42,
    "&:hover": { borderColor: "#9ca3af" },
  }),
  multiValue: (base: Record<string, unknown>) => ({
    ...base,
    backgroundColor: "#fff3e0",
    borderRadius: 4,
  }),
  multiValueLabel: (base: Record<string, unknown>) => ({
    ...base,
    color: "#c62800",
    fontSize: "0.85rem",
  }),
  multiValueRemove: (base: Record<string, unknown>) => ({
    ...base,
    color: "#c62800",
    "&:hover": { backgroundColor: "#ffe0b2", color: "#ff4f00" },
  }),
};

const isValidEmail = (email: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const isValidPhone = (phone: string) => phone.replace(/\D/g, "").length >= 7;

const FilterRecipientsModal = ({
  isOpen,
  filters,
  selectedCustomers,
  onApply,
  onCancel,
  campaignType = "email",
}: FilterRecipientsModalProps) => {
  const { t } = useTranslation();
  const tBase = "views.outreach.campaigns.filters";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const [activeTab, setActiveTab] = useState<"filters" | "recipients">("filters");
  const [state, dispatch] = useFilterModalReducer(isOpen, filters, selectedCustomers);

  useEffect(() => {
    if (isOpen) {
      setActiveTab("filters");
    }
  }, [isOpen]);

  const { data: filterOptions, isLoading: isOptionsLoading } =
    useQuery<FilterOptions>("campaign-filter-options", getFilterOptions, {
      enabled: isOpen,
    });

  // --- Recipient exclusion (Feature #1) ---
  // Maintained locally in the modal; merged into filters on Apply.
  // Recipients are still fetched from the API without the exclude so the
  // "Removed" section can display them for re-add.
  const [excludedIds, setExcludedIds] = useState<Set<string>>(
    () => new Set((filters.excludeCustomerIds || []).map(String))
  );

  const { liveCount, liveSkipped, isLiveCountLoading, recipients, isRecipientsLoading } =
    useRecipientPreview(
      state.localFilters,
      campaignType,
      isOpen,
      activeTab,
      excludedIds.size > 0 ? Array.from(excludedIds).map(Number) : null
    );

  // Reset when modal opens with fresh filters
  useEffect(() => {
    if (isOpen) {
      setExcludedIds(new Set((filters.excludeCustomerIds || []).map(String)));
    }
  }, [isOpen, filters.excludeCustomerIds]);

  const handleRemoveRecipient = useCallback((bcustomerId: string) => {
    setExcludedIds((prev) => new Set([...prev, bcustomerId]));
  }, []);

  const handleReAddRecipient = useCallback((bcustomerId: string) => {
    setExcludedIds((prev) => {
      const next = new Set(prev);
      next.delete(bcustomerId);
      return next;
    });
  }, []);

  // The count comes from the server with the live exclusions applied — no
  // client-side subtraction (stale exclusions from cloned campaigns made
  // liveCount - excludedIds.size show fewer recipients than actually match).

  const handleCustomersChange = useCallback(
    (customers: SelectedCustomer[], ids: number[] | null) => {
      dispatch({ type: "SET_CUSTOMERS", customers });
      dispatch({ type: "UPDATE_FILTER", key: "includeCustomerIds", value: ids });
    },
    [dispatch]
  );

  const { loadOptions, handleSelect, selectValue } = useCustomerSearch(
    state.localCustomers,
    handleCustomersChange
  );

  // -- Manual entry → existing-customer match picker --
  // A typed phone/email that matches existing customers is never attached
  // automatically: the user picks the customer (added as a normal customer
  // recipient with merge data) or explicitly adds the raw value without one.
  const [matchPicker, setMatchPicker] = useState<{
    type: "email" | "phone";
    value: string;
    candidates: Recipient[];
  } | null>(null);
  const [isLookingUp, setIsLookingUp] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setMatchPicker(null);
    }
  }, [isOpen]);

  const attachCandidate = (candidate: Recipient) => {
    if (!matchPicker || !candidate.bcustomerId) {
      return;
    }
    const id = Number(candidate.bcustomerId);
    const alreadySelected = state.localCustomers.some((c) => c.id === id);
    if (!alreadySelected) {
      const name =
        [candidate.firstName, candidate.lastName].filter(Boolean).join(" ") ||
        candidate.companyName ||
        candidate.email;
      const updated = [
        ...state.localCustomers,
        {
          id,
          name,
          email: candidate.email,
          companyName: candidate.companyName || undefined,
        },
      ];
      handleCustomersChange(
        updated,
        updated.map((c) => c.id)
      );
    }
    if (matchPicker.type === "email") {
      dispatch({ type: "SET_EMAIL_INPUT", value: "" });
    } else {
      dispatch({ type: "SET_PHONE_INPUT", value: "" });
    }
    setMatchPicker(null);
  };

  const addWithoutCustomer = () => {
    if (!matchPicker) {
      return;
    }
    if (matchPicker.type === "email") {
      dispatch({ type: "ADD_MANUAL_EMAIL", email: matchPicker.value });
    } else {
      dispatch({ type: "ADD_MANUAL_PHONE", phone: matchPicker.value });
    }
    setMatchPicker(null);
  };

  // -- Manual emails --
  const addManualEmail = async () => {
    const email = state.emailInput.trim().toLowerCase();
    if (!email) {
      return;
    }
    if (!isValidEmail(email)) {
      dispatch({ type: "SET_EMAIL_ERROR", error: tr("invalidEmail") });
      return;
    }
    if ((state.localFilters.manualEmails || []).includes(email)) {
      dispatch({ type: "SET_EMAIL_ERROR", error: tr("duplicateEmail") });
      return;
    }
    setIsLookingUp(true);
    try {
      const candidates = await lookupRecipientContact(email, "email");
      if (candidates.length > 0) {
        setMatchPicker({ type: "email", value: email, candidates });
        return;
      }
    } catch {
      // Lookup unavailable — fall back to a plain manual entry.
    } finally {
      setIsLookingUp(false);
    }
    dispatch({ type: "ADD_MANUAL_EMAIL", email });
  };

  const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addManualEmail();
    }
  };

  // -- Manual phones --
  const addManualPhone = async () => {
    const phone = state.phoneInput.trim();
    if (!phone) {
      return;
    }
    if (!isValidPhone(phone)) {
      dispatch({ type: "SET_PHONE_ERROR", error: tr("invalidPhone") });
      return;
    }
    if ((state.localFilters.manualPhones || []).includes(phone)) {
      dispatch({ type: "SET_PHONE_ERROR", error: tr("duplicatePhone") });
      return;
    }
    setIsLookingUp(true);
    try {
      const candidates = await lookupRecipientContact(phone, "phone");
      if (candidates.length > 0) {
        setMatchPicker({ type: "phone", value: phone, candidates });
        return;
      }
    } catch {
      // Lookup unavailable — fall back to a plain manual entry.
    } finally {
      setIsLookingUp(false);
    }
    dispatch({ type: "ADD_MANUAL_PHONE", phone });
  };

  const handlePhoneKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addManualPhone();
    }
  };

  // Inline panel shown when a typed value matches existing customers.
  const renderMatchPicker = (type: "email" | "phone") =>
    matchPicker && matchPicker.type === type ? (
      <div className="mt-3 p-3 border border-amber-300 bg-amber-50 rounded-lg space-y-2">
        <p className="text-sm text-amber-900">
          {tr(type === "email" ? "matchFoundEmail" : "matchFoundPhone")}
        </p>
        <div className="divide-y divide-amber-200">
          {matchPicker.candidates.map((c) => (
            <div
              key={c.bcustomerId ?? c.email}
              className="flex flex-wrap items-center justify-between gap-2 py-2"
            >
              <div className="text-sm text-gray-800 min-w-0">
                <span className="font-medium">
                  {[c.firstName, c.lastName].filter(Boolean).join(" ") ||
                    c.companyName ||
                    "-"}
                </span>
                <span className="text-gray-500 break-all">
                  {" "}
                  · {c.email || "-"} · {c.phone || "-"}
                  {c.city ? ` · ${c.city}${c.state ? `, ${c.state}` : ""}` : ""}
                </span>
              </div>
              <Button primary size="small" onClick={() => attachCandidate(c)}>
                {tr("selectCustomer")}
              </Button>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <OutlineButton size="small" onClick={addWithoutCustomer}>
            {tr("addWithoutCustomer")}
          </OutlineButton>
          <button
            type="button"
            className="text-sm text-gray-500 hover:text-gray-700 px-2"
            onClick={() => setMatchPicker(null)}
          >
            {tr("cancel")}
          </button>
        </div>
      </div>
    ) : null;

  // -- Select / remove helpers for TypeaheadFilter --
  const selectItem = (filterKey: keyof CampaignFilters) => (value: number | string) => {
    const current = (state.localFilters[filterKey] as (number | string)[] | null) || [];
    if (!current.includes(value)) {
      dispatch({ type: "TOGGLE_ITEM", filterKey, value });
    }
  };

  const removeItem = (filterKey: keyof CampaignFilters) => (value: number | string) => {
    const current = (state.localFilters[filterKey] as (number | string)[] | null) || [];
    if (current.includes(value)) {
      dispatch({ type: "TOGGLE_ITEM", filterKey, value });
    }
  };

  // -- Location bi-directional cascading logic --
  // Any selection at any level narrows the options at all other levels.
  const locationCombos = filterOptions?.locationCombos || [];

  const includedStates = state.localFilters.includeStates || [];
  const includedCities = state.localFilters.includeCities || [];
  const includedZips = state.localFilters.includeZipCodes || [];

  const stateItems = (() => {
    let combos = locationCombos;
    if (includedCities.length > 0) {
      combos = combos.filter((lc) => includedCities.includes(lc.city));
    }
    if (includedZips.length > 0) {
      combos = combos.filter((lc) => includedZips.includes(lc.zipcode));
    }
    const unique = [...new Set(combos.map((lc) => lc.state))].sort();
    return unique.map((s) => ({ value: s, label: s }));
  })();

  const cityItems = (() => {
    let combos = locationCombos;
    if (includedStates.length > 0) {
      combos = combos.filter((lc) => includedStates.includes(lc.state));
    }
    if (includedZips.length > 0) {
      combos = combos.filter((lc) => includedZips.includes(lc.zipcode));
    }
    const unique = [...new Set(combos.map((lc) => lc.city))].sort();
    return unique.map((c) => ({ value: c, label: c }));
  })();

  const zipItems = (() => {
    let combos = locationCombos;
    if (includedStates.length > 0) {
      combos = combos.filter((lc) => includedStates.includes(lc.state));
    }
    if (includedCities.length > 0) {
      combos = combos.filter((lc) => includedCities.includes(lc.city));
    }
    const unique = [...new Set(combos.map((lc) => lc.zipcode))].sort();
    return unique.map((z) => ({ value: z, label: z }));
  })();

  // Location: exclude hides included items, include hides excluded items
  const excludedStates = state.localFilters.excludeStates || [];
  const excludedCities = state.localFilters.excludeCities || [];
  const excludedZips = state.localFilters.excludeZipCodes || [];

  const includeStateItems = stateItems.filter((s) => !excludedStates.includes(s.value));
  const excludeStateItems = stateItems.filter((s) => !includedStates.includes(s.value));
  const includeCityItems = cityItems.filter((c) => !excludedCities.includes(c.value));
  const excludeCityItems = cityItems.filter((c) => !includedCities.includes(c.value));
  const includeZipItems = zipItems.filter((z) => !excludedZips.includes(z.value));
  const excludeZipItems = zipItems.filter((z) => !includedZips.includes(z.value));

  // Tags, plans, types: bidirectional — include hides from exclude AND exclude hides from include
  const allCustomerTagItems = (filterOptions?.customerTags || []).map((t) => ({
    value: t.id,
    label: t.name,
    color: t.color || null,
  }));
  const customerTagItems = allCustomerTagItems.filter(
    (t) => !(state.localFilters.excludeTags || []).includes(t.value)
  );
  const excludeCustomerTagItems = allCustomerTagItems.filter(
    (t) => !(state.localFilters.includeTags || []).includes(t.value)
  );

  const allPropertyTagItems = (filterOptions?.propertyTags || []).map((t) => ({
    value: t.id,
    label: t.name,
    color: t.color || null,
  }));
  const propertyTagItems = allPropertyTagItems.filter(
    (t) => !(state.localFilters.excludePropertyTags || []).includes(t.value)
  );
  const excludePropertyTagItems = allPropertyTagItems.filter(
    (t) => !(state.localFilters.includePropertyTags || []).includes(t.value)
  );

  const allServicePlanItems = (filterOptions?.servicePlans || []).map((p) => ({
    value: p.id,
    label: p.name,
  }));
  const servicePlanItems = allServicePlanItems.filter(
    (p) => !(state.localFilters.excludeServicePlanIds || []).includes(p.value)
  );
  const excludeServicePlanItems = allServicePlanItems.filter(
    (p) => !(state.localFilters.servicePlanIds || []).includes(p.value)
  );

  const allAppointmentTypeItems = (filterOptions?.appointmentTypes || []).map(
    (at) => ({ value: at.id, label: at.name })
  );
  const appointmentTypeItems = allAppointmentTypeItems.filter(
    (at) => !(state.localFilters.excludeAppointmentTypeIds || []).includes(at.value)
  );
  const excludeAppointmentTypeItems = allAppointmentTypeItems.filter(
    (at) => !(state.localFilters.appointmentTypeIds || []).includes(at.value)
  );

  const allRequestTypeItems = (filterOptions?.requestTypes || []).map((rt) => ({
    value: rt.id,
    label: rt.name,
  }));
  const requestTypeItems = allRequestTypeItems.filter(
    (rt) => !(state.localFilters.excludeRequestTypeIds || []).includes(rt.value)
  );
  const excludeRequestTypeItems = allRequestTypeItems.filter(
    (rt) => !(state.localFilters.requestTypeIds || []).includes(rt.value)
  );

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="border-b">
          <div className="flex items-center justify-between px-6 pt-6 pb-3">
            <h2 className="text-xl font-bold text-gray-800">{tr("title")}</h2>
            <button
              className="text-gray-400 hover:text-gray-600 text-2xl"
              onClick={onCancel}
            >
              &times;
            </button>
          </div>
          {/* Tabs */}
          <div className="flex px-6 gap-6">
            {(["filters", "recipients"] as const).map((tab) => (
              <button
                key={tab}
                className={`pb-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab
                    ? "border-[#ff4f00] text-[#ff4f00]"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
                onClick={() => setActiveTab(tab)}
              >
                {tr(tab === "filters" ? "tabFilters" : "tabRecipients")}
                {tab === "recipients" && liveCount !== null && (
                  <span className="ml-1 text-xs">({liveCount})</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto p-6 space-y-3 flex-1">
          {/* Recipients Tab */}
          {activeTab === "recipients" && (
            <RecipientsTable
              recipients={recipients}
              isLoading={isRecipientsLoading}
              campaignType={campaignType}
              excludedIds={excludedIds}
              onRemove={handleRemoveRecipient}
              onReAdd={handleReAddRecipient}
            />
          )}

          {/* Filters Tab */}
          {activeTab === "filters" &&
            (isOptionsLoading ? (
              <p className="text-gray-500">{tr("loadingOptions")}</p>
            ) : (
              <>
                {/* Specific Customers */}
                <CollapsibleSection title={tr("specificCustomers")}>
                  <p className="text-sm text-gray-500 mb-3">
                    {tr("specificCustomersHelp")}
                  </p>
                  <AsyncSelect<CustomerOption, true>
                    isMulti
                    cacheOptions
                    defaultOptions={false}
                    loadOptions={loadOptions}
                    value={selectValue}
                    onChange={(selected) => handleSelect(selected || [])}
                    placeholder={tr("searchCustomersPlaceholder")}
                    noOptionsMessage={({ inputValue }) =>
                      inputValue.length < 2
                        ? tr("typeToSearch")
                        : tr("noCustomersFound")
                    }
                    styles={ASYNC_SELECT_STYLES}
                  />
                </CollapsibleSection>

                {/* Manual Emails (email campaigns only) */}
                {campaignType !== "sms" && (
                  <CollapsibleSection title={tr("manualEmails")}>
                    <p className="text-sm text-gray-500 mb-3">
                      {tr("manualEmailsHelp")}
                    </p>
                    <div className="flex gap-2">
                      <input
                        type="email"
                        className={`flex-1 p-2 border rounded-lg text-sm ${
                          state.emailError
                            ? "border-red-400"
                            : "border-gray-300"
                        }`}
                        placeholder={tr("manualEmailPlaceholder")}
                        value={state.emailInput}
                        onChange={(e) =>
                          dispatch({
                            type: "SET_EMAIL_INPUT",
                            value: e.target.value,
                          })
                        }
                        onKeyDown={handleEmailKeyDown}
                      />
                      <Button
                        primary
                        size="small"
                        isLoading={isLookingUp}
                        onClick={addManualEmail}
                      >
                        {tr("addEmail")}
                      </Button>
                    </div>
                    {state.emailError && (
                      <p className="text-red-500 text-sm mt-1">
                        {state.emailError}
                      </p>
                    )}
                    {renderMatchPicker("email")}
                    {state.localFilters.manualEmails &&
                      state.localFilters.manualEmails.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {state.localFilters.manualEmails.map((email) => (
                            <span
                              key={email}
                              className="inline-flex items-center gap-1 px-3 py-1 bg-orange-50 text-orange-800 text-sm rounded-full border border-orange-200"
                            >
                              {email}
                              <button
                                type="button"
                                className="text-orange-500 hover:text-orange-700 font-bold ml-1"
                                onClick={() =>
                                  dispatch({
                                    type: "REMOVE_MANUAL_EMAIL",
                                    email,
                                  })
                                }
                              >
                                &times;
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                  </CollapsibleSection>
                )}

                {/* Manual Phone Numbers (SMS campaigns only) */}
                {campaignType === "sms" && (
                  <CollapsibleSection title={tr("manualPhones")}>
                    <p className="text-sm text-gray-500 mb-3">
                      {tr("manualPhonesHelp")}
                    </p>
                    <div className="flex gap-2">
                      <input
                        type="tel"
                        className={`flex-1 p-2 border rounded-lg text-sm ${
                          state.phoneError
                            ? "border-red-400"
                            : "border-gray-300"
                        }`}
                        placeholder={tr("manualPhonePlaceholder")}
                        value={state.phoneInput}
                        onChange={(e) =>
                          dispatch({
                            type: "SET_PHONE_INPUT",
                            value: e.target.value,
                          })
                        }
                        onKeyDown={handlePhoneKeyDown}
                      />
                      <Button
                        primary
                        size="small"
                        isLoading={isLookingUp}
                        onClick={addManualPhone}
                      >
                        {tr("addPhone")}
                      </Button>
                    </div>
                    {state.phoneError && (
                      <p className="text-red-500 text-sm mt-1">
                        {state.phoneError}
                      </p>
                    )}
                    {renderMatchPicker("phone")}
                    {state.localFilters.manualPhones &&
                      state.localFilters.manualPhones.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {state.localFilters.manualPhones.map((phone) => (
                            <span
                              key={phone}
                              className="inline-flex items-center gap-1 px-3 py-1 bg-orange-50 text-orange-800 text-sm rounded-full border border-orange-200"
                            >
                              {phone}
                              <button
                                type="button"
                                className="text-orange-500 hover:text-orange-700 font-bold ml-1"
                                onClick={() =>
                                  dispatch({
                                    type: "REMOVE_MANUAL_PHONE",
                                    phone,
                                  })
                                }
                              >
                                &times;
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                  </CollapsibleSection>
                )}

                {/* Location Filters */}
                <CollapsibleSection title={tr("location")}>
                  <div className="space-y-4">
                    {/* States */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">
                          {tr("includeStates")}
                        </label>
                        <TypeaheadFilter
                          items={includeStateItems}
                          selectedValues={state.localFilters.includeStates}
                          onSelect={selectItem("includeStates")}
                          onRemove={removeItem("includeStates")}
                          emptyMessage={tr("noOptionsAvailable")}
                          placeholder={tr("searchStates")}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">
                          {tr("excludeStates")}
                        </label>
                        <TypeaheadFilter
                          items={excludeStateItems}
                          selectedValues={state.localFilters.excludeStates}
                          onSelect={selectItem("excludeStates")}
                          onRemove={removeItem("excludeStates")}
                          emptyMessage={tr("noOptionsAvailable")}
                          placeholder={tr("searchStates")}
                        />
                      </div>
                    </div>

                    <hr className="border-gray-200" />

                    {/* Cities */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">
                          {tr("includeCities")}
                        </label>
                        <TypeaheadFilter
                          items={includeCityItems}
                          selectedValues={state.localFilters.includeCities}
                          onSelect={selectItem("includeCities")}
                          onRemove={removeItem("includeCities")}
                          emptyMessage={tr("noOptionsAvailable")}
                          placeholder={tr("searchCities")}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">
                          {tr("excludeCities")}
                        </label>
                        <TypeaheadFilter
                          items={excludeCityItems}
                          selectedValues={state.localFilters.excludeCities}
                          onSelect={selectItem("excludeCities")}
                          onRemove={removeItem("excludeCities")}
                          emptyMessage={tr("noOptionsAvailable")}
                          placeholder={tr("searchCities")}
                        />
                      </div>
                    </div>

                    <hr className="border-gray-200" />

                    {/* Zip Codes */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">
                          {tr("includeZipCodes")}
                        </label>
                        <TypeaheadFilter
                          items={includeZipItems}
                          selectedValues={state.localFilters.includeZipCodes}
                          onSelect={selectItem("includeZipCodes")}
                          onRemove={removeItem("includeZipCodes")}
                          emptyMessage={tr("noOptionsAvailable")}
                          placeholder={tr("searchZipCodes")}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">
                          {tr("excludeZipCodes")}
                        </label>
                        <TypeaheadFilter
                          items={excludeZipItems}
                          selectedValues={state.localFilters.excludeZipCodes}
                          onSelect={selectItem("excludeZipCodes")}
                          onRemove={removeItem("excludeZipCodes")}
                          emptyMessage={tr("noOptionsAvailable")}
                          placeholder={tr("searchZipCodes")}
                        />
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">{tr("matchAnyHint")}</p>
                </CollapsibleSection>

                {/* Customer Tags */}
                <CollapsibleSection title={tr("customerTags")}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        {tr("includeTags")}
                      </label>
                      <TypeaheadFilter
                        items={customerTagItems}
                        selectedValues={state.localFilters.includeTags}
                        onSelect={selectItem("includeTags")}
                        onRemove={removeItem("includeTags")}
                        emptyMessage={tr("noTagsAvailable")}
                        placeholder={tr("searchTags")}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        {tr("excludeTags")}
                      </label>
                      <TypeaheadFilter
                        items={excludeCustomerTagItems}
                        selectedValues={state.localFilters.excludeTags}
                        onSelect={selectItem("excludeTags")}
                        onRemove={removeItem("excludeTags")}
                        emptyMessage={tr("noTagsAvailable")}
                        placeholder={tr("searchTags")}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">{tr("matchAnyHint")}</p>
                </CollapsibleSection>

                {/* Property Tags */}
                <CollapsibleSection title={tr("propertyTags")}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        {tr("includeTags")}
                      </label>
                      <TypeaheadFilter
                        items={propertyTagItems}
                        selectedValues={state.localFilters.includePropertyTags}
                        onSelect={selectItem("includePropertyTags")}
                        onRemove={removeItem("includePropertyTags")}
                        emptyMessage={tr("noTagsAvailable")}
                        placeholder={tr("searchTags")}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        {tr("excludeTags")}
                      </label>
                      <TypeaheadFilter
                        items={excludePropertyTagItems}
                        selectedValues={state.localFilters.excludePropertyTags}
                        onSelect={selectItem("excludePropertyTags")}
                        onRemove={removeItem("excludePropertyTags")}
                        emptyMessage={tr("noTagsAvailable")}
                        placeholder={tr("searchTags")}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">{tr("matchAnyHint")}</p>
                </CollapsibleSection>

                {/* Customer Status */}
                <CollapsibleSection title={tr("customerStatus")}>
                  <div className="flex gap-6">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="radio"
                        name="activeStatus"
                        checked={state.localFilters.activeStatus === null}
                        onChange={() =>
                          dispatch({
                            type: "UPDATE_FILTER",
                            key: "activeStatus",
                            value: null,
                          })
                        }
                      />
                      {tr("status_none")}
                    </label>
                    {(["active", "inactive"] as const).map((status) => (
                      <label
                        key={status}
                        className="flex items-center gap-2 text-sm cursor-pointer"
                      >
                        <input
                          type="radio"
                          name="activeStatus"
                          checked={
                            state.localFilters.activeStatus === status
                          }
                          onChange={() =>
                            dispatch({
                              type: "UPDATE_FILTER",
                              key: "activeStatus",
                              value: status,
                            })
                          }
                        />
                        {tr(`status_${status}`)}
                      </label>
                    ))}
                  </div>
                </CollapsibleSection>

                {/* Service Plans */}
                <CollapsibleSection title={tr("servicePlans")}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        {tr("includeTags")}
                      </label>
                      <TypeaheadFilter
                        items={servicePlanItems}
                        selectedValues={state.localFilters.servicePlanIds}
                        onSelect={selectItem("servicePlanIds")}
                        onRemove={removeItem("servicePlanIds")}
                        emptyMessage={tr("noServicePlansAvailable")}
                        placeholder={tr("searchServicePlans")}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        {tr("excludeTags")}
                      </label>
                      <TypeaheadFilter
                        items={excludeServicePlanItems}
                        selectedValues={state.localFilters.excludeServicePlanIds}
                        onSelect={selectItem("excludeServicePlanIds")}
                        onRemove={removeItem("excludeServicePlanIds")}
                        emptyMessage={tr("noServicePlansAvailable")}
                        placeholder={tr("searchServicePlans")}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mb-3">{tr("matchAnyHint")}</p>
                  <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-gray-600">
                      {tr("expiringWithin")}
                    </label>
                    <input
                      type="number"
                      className="w-24 p-2 border border-gray-300 rounded-lg text-sm"
                      min={0}
                      placeholder="0"
                      value={
                        state.localFilters.servicePlanExpiringDays ?? ""
                      }
                      onChange={(e) =>
                        dispatch({
                          type: "UPDATE_FILTER",
                          key: "servicePlanExpiringDays",
                          value: e.target.value
                            ? Number(e.target.value)
                            : null,
                        })
                      }
                    />
                    <span className="text-sm text-gray-500">
                      {tr("days")}
                    </span>
                  </div>
                </CollapsibleSection>

                {/* Appointments */}
                <CollapsibleSection title={tr("appointments")}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        {tr("lastApptBefore")}
                      </label>
                      <input
                        type="date"
                        className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                        value={state.localFilters.lastApptBefore || ""}
                        onChange={(e) =>
                          dispatch({
                            type: "UPDATE_FILTER",
                            key: "lastApptBefore",
                            value: e.target.value || null,
                          })
                        }
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        {tr("lastApptAfter")}
                      </label>
                      <input
                        type="date"
                        className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                        value={state.localFilters.lastApptAfter || ""}
                        onChange={(e) =>
                          dispatch({
                            type: "UPDATE_FILTER",
                            key: "lastApptAfter",
                            value: e.target.value || null,
                          })
                        }
                      />
                    </div>
                  </div>
                </CollapsibleSection>

                {/* Appointment Types */}
                <CollapsibleSection title={tr("appointmentTypes")}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        {tr("includeTags")}
                      </label>
                      <TypeaheadFilter
                        items={appointmentTypeItems}
                        selectedValues={state.localFilters.appointmentTypeIds}
                        onSelect={selectItem("appointmentTypeIds")}
                        onRemove={removeItem("appointmentTypeIds")}
                        emptyMessage={tr("noOptionsAvailable")}
                        placeholder={tr("searchAppointmentTypes")}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        {tr("excludeTags")}
                      </label>
                      <TypeaheadFilter
                        items={excludeAppointmentTypeItems}
                        selectedValues={state.localFilters.excludeAppointmentTypeIds}
                        onSelect={selectItem("excludeAppointmentTypeIds")}
                        onRemove={removeItem("excludeAppointmentTypeIds")}
                        emptyMessage={tr("noOptionsAvailable")}
                        placeholder={tr("searchAppointmentTypes")}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">{tr("matchAnyHint")}</p>
                </CollapsibleSection>

                {/* Request Types */}
                <CollapsibleSection title={tr("requestTypes")}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        {tr("includeTags")}
                      </label>
                      <TypeaheadFilter
                        items={requestTypeItems}
                        selectedValues={state.localFilters.requestTypeIds}
                        onSelect={selectItem("requestTypeIds")}
                        onRemove={removeItem("requestTypeIds")}
                        emptyMessage={tr("noOptionsAvailable")}
                        placeholder={tr("searchRequestTypes")}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        {tr("excludeTags")}
                      </label>
                      <TypeaheadFilter
                        items={excludeRequestTypeItems}
                        selectedValues={state.localFilters.excludeRequestTypeIds}
                        onSelect={selectItem("excludeRequestTypeIds")}
                        onRemove={removeItem("excludeRequestTypeIds")}
                        emptyMessage={tr("noOptionsAvailable")}
                        placeholder={tr("searchRequestTypes")}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">{tr("matchAnyHint")}</p>
                </CollapsibleSection>
              </>
            ))}
        </div>

        {/* Footer */}
        <div className="border-t p-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">
              {tr("matchingRecipients")}:
            </span>
            {isLiveCountLoading ? (
              <span className="text-sm text-gray-400">{tr("counting")}</span>
            ) : (
              <span className="text-lg font-bold text-[#ff4f00]">
                {liveCount ?? "--"}
              </span>
            )}
            {/* Why the number is smaller than the filters imply. Without this
                the count simply shrinks with no explanation — on production
                one business's filters match 47,399 customers and reach 30,814,
                because 16,556 have no email address at all (ESERV2-1467). */}
            {!isLiveCountLoading && (liveSkipped?.total ?? 0) > 0 && (
              <span className="text-xs text-gray-500">
                {liveSkipped?.total} {tr("cannotReceive")}
                {" — "}
                {[
                  (liveSkipped?.noAddress ?? 0) > 0 &&
                    `${liveSkipped?.noAddress} ${tr("skippedNoAddress")}`,
                  (liveSkipped?.optedOut ?? 0) > 0 &&
                    `${liveSkipped?.optedOut} ${tr("skippedOptedOut")}`,
                  (liveSkipped?.suppressed ?? 0) > 0 &&
                    `${liveSkipped?.suppressed} ${tr("skippedUndeliverable")}`,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            )}
          </div>
          <div className="flex gap-3">
            <OutlineButton
              size="small"
              onClick={() => dispatch({ type: "RESET" })}
            >
              {tr("reset")}
            </OutlineButton>
            <OutlineButton size="small" onClick={onCancel}>
              {tr("cancel")}
            </OutlineButton>
            <Button
              primary
              size="small"
              onClick={() => {
                const mergedFilters: CampaignFilters = {
                  ...state.localFilters,
                  excludeCustomerIds:
                    excludedIds.size > 0
                      ? [...excludedIds].map(Number)
                      : null,
                };
                onApply(mergedFilters, state.localCustomers);
              }}
            >
              {tr("apply")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterRecipientsModal;
